import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormSubmitted(true);
      setFormError('');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9nfsrobdkpk99j7gj10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      const parsed = JSON.parse(responseText);

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        setFormError('');
        form.reset();
      } else {
        setFormError(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '久久影视的剧集是免费观看的吗？',
        acceptedAnswer: { '@type': 'Answer', 'text': '是的，久久影视所有电视剧全集均为免费在线观看，无需注册会员，无任何隐藏收费。' },
      },
      {
        '@type': 'Question',
        name: '观看需要安装播放器吗？',
        acceptedAnswer: { '@type': 'Answer', 'text': '不需要。久久影视采用最新的HTML5播放技术，打开网页即可观看，兼容所有主流浏览器。' },
      },
      {
        '@type': 'Question',
        name: '支持哪些设备观看？',
        acceptedAnswer: { '@type': 'Answer', 'text': '久久影视支持电脑、平板、手机等多种设备观看，自适应屏幕尺寸，随时随地追剧。' },
      },
      {
        '@type': 'Question',
        name: '剧集画质如何？',
        acceptedAnswer: { '@type': 'Answer', 'text': '我们提供720P至1080P高清画质，加载速度快，播放流畅不卡顿。' },
      },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '久久影视',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '久久影视汇集电视剧全集免费在线观看，热门国产剧韩剧全集秒播，无需等待更新，一次性看完整部剧，高清不卡顿。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@jiujiutv.com',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([faqSchema, orgSchema]) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium">关于我们</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于<strong>久久影视</strong>
            </h1>
            <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              久久影视是领先的电视剧全集免费在线观看平台，致力于为广大剧迷提供高清、流畅、便捷的追剧体验。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <div className="p-6 rounded-xl bg-background-100 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-eye-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  让每一个人都能轻松享受追剧的乐趣，打破付费壁垒，让优质内容触手可及。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-star-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">核心优势</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  全集一次性放出，高清不卡顿，秒播加载，无需等待更新，追剧更畅快。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-team-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">团队理念</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  以用户为中心，持续优化观影体验，打造国内最好的免费追剧平台。
                </p>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                品牌故事
              </h2>
              <div className="p-6 md:p-8 rounded-xl bg-background-100">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  久久影视创立于2020年，由一群热爱影视的年轻人共同打造。我们深知追剧爱好者的痛点——等待更新太煎熬、卡顿影响体验、四处找资源太麻烦。于是，久久影视应运而生。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  我们专注于聚合优质的电视剧内容，涵盖热门国产剧、韩剧、美剧、日剧等多个品类。通过自研的高效视频加载技术，实现了秒播级别的播放体验，让用户告别缓冲等待。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  截至目前，久久影视已累计服务超过<strong>千万级用户</strong>，日均播放量突破<strong>500万次</strong>。我们始终相信，好的内容应该被更多人看到，好的体验不需要付费。
                </p>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {[
                  { name: '追剧达人小王', text: '用了久久影视之后再也不用到处找资源了！全集一次性放出，画质清晰，加载超级快，强烈推荐！', rating: 5 },
                  { name: '韩剧迷妹', text: '韩剧资源太全了，更新速度跟韩国同步，而且免费不收费，简直是韩剧迷的福音！', rating: 5 },
                  { name: '社畜追剧人', text: '下班回家就想躺着追剧，久久影视的手机版体验太好了，自动适配屏幕，操作也简单。', rating: 5 },
                  { name: '资深影评人老张', text: '作为一名影视爱好者，久久影视的片源质量和播放稳定性让我非常满意，国产剧分类也很专业。', rating: 4 },
                  { name: '学生党小刘', text: '学生党没钱充会员，久久影视真的是救星！免费高清看全集，宿舍里大家都在用。', rating: 5 },
                  { name: '全职妈妈阿花', text: '带娃间隙追剧解压，久久影视操作简单，不用学习就能上手，片库更新也快。', rating: 5 },
                ].map((review, idx) => (
                  <div key={idx} className="p-5 rounded-xl bg-background-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      "{review.text}"
                    </p>
                    <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  { q: '久久影视的剧集是免费观看的吗？', a: '是的，久久影视所有电视剧全集均为免费在线观看，无需注册会员，无任何隐藏收费。' },
                  { q: '观看需要安装播放器吗？', a: '不需要。久久影视采用最新的HTML5播放技术，打开网页即可观看，兼容所有主流浏览器。' },
                  { q: '支持哪些设备观看？', a: '久久影视支持电脑、平板、手机等多种设备观看，自适应屏幕尺寸，随时随地追剧。' },
                  { q: '剧集画质如何？', a: '我们提供720P至1080P高清画质，加载速度快，播放流畅不卡顿。' },
                  { q: '剧集多久更新一次？', a: '我们的内容团队每日更新剧库，热门新剧上线后第一时间同步，确保您不错过任何精彩。' },
                ].map((faq, idx) => (
                  <details key={idx} className="group p-5 rounded-xl bg-background-100 cursor-pointer">
                    <summary className="flex items-center justify-between text-sm font-medium text-foreground-900 list-none">
                      {faq.q}
                      <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                    </summary>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">service@jiujiutv.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                      <i className="ri-customer-service-2-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h4>
                      <p className="text-sm text-foreground-500">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                      <i className="ri-time-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">服务时间</h4>
                      <p className="text-sm text-foreground-500">周一至周日 9:00 - 22:00</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-4">给我们留言</h4>
                  {formSubmitted ? (
                    <div className="p-6 rounded-xl bg-secondary-100 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-200">
                        <i className="ri-check-line text-xl text-secondary-600"></i>
                      </div>
                      <p className="text-sm text-secondary-800 font-medium">感谢您的留言！</p>
                      <p className="text-xs text-secondary-600 mt-1">我们会尽快回复您</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="您的姓名"
                        className="w-full h-11 px-4 text-sm rounded-lg bg-background-100 border border-background-300 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="您的邮箱"
                        className="w-full h-11 px-4 text-sm rounded-lg bg-background-100 border border-background-300 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        placeholder="请输入您的留言（最多500字）"
                        className="w-full px-4 py-3 text-sm rounded-lg bg-background-100 border border-background-300 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      ></textarea>

                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />

                      {formError && (
                        <p className="text-xs text-red-500">{formError}</p>
                      )}

                      <button
                        type="submit"
                        className="w-full h-11 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                      >
                        发送留言
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}